import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { courseAPI } from '../api/courseApi';
import { getImageUrl } from '../utils/helpers';
import Loader from '../components/Loader';
import { toast } from 'react-toastify';

const Subjects = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);

  // Get params from navigation state (matching mobile app)
  const { type, subject: subjectName, flag, viewType, id } = location.state || {};
  
  // Use the id from state if available, otherwise use courseId from URL
  const actualCourseId = id || courseId;

  useEffect(() => {
    if (actualCourseId) {
      fetchSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualCourseId]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      console.log('Fetching subjects:', { actualCourseId, type, flag, viewType });
      
      // Mobile app uses /getSubjectById/{id}
      const response = await courseAPI.getSubjectById(actualCourseId);
      console.log('Subjects Response:', response);
      
      if (response && response.data) {
        setSubjects(response.data);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      toast.error(error.response?.data?.message || 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  // Navigate based on course type (matching mobile app logic)
  const handleSubjectClick = useCallback((item) => {
    console.log('Subject clicked:', { item, type, viewType, flag });
    
    // Check if user has purchased (flag === 1 means NOT purchased)
    if (flag === 1) {
      toast.error('Please purchase this course to access the content');
      return;
    }
    
    // Check if it's a 'Free Videos' type
    if (viewType === 'Free Videos') {
      console.log('→ Navigating to Free Videos (direct to videos)');
      // Navigate directly to videos for free content
      navigate(`/videos/${item.id}`, {
        state: {
          id: item.id,
          type: 'Free Videos',
          name: item.subname,
          category: item.category,
          flag,
        }
      });
      return;
    }

    // Main conditional logic: type='new' → 4-screen flow, else → 2-screen flow
    if (type === 'new') {
      console.log('→ Type is "new": Using 4-screen flow (Subject → SubjectCategory → SubjectSubCategory → Videos)');
      // 4-screen flow: Subject → SubjectCategory → SubjectSubCategory → Videos
      navigate(`/subject-category/${item.id}`, {
        state: {
          courseId: item.id,
          type: item.type,
          name: item.subname,
          flag,
        }
      });
    } else {
      console.log('→ Type is "old" or regular: Using 2-screen flow (Subject → Videos)');
      // 2-screen flow: Subject → Videos (direct)
      navigate(`/videos/${item.id}`, {
        state: {
          id: item.id,
          type: item.type,
          name: item.subname,
          category: item.category,
          flag,
        }
      });
    }
  }, [navigate, flag, type, viewType]);

  if (loading) return <Loader />;

  // If not purchased (flag === 1), show purchase required message
  if (flag === 1) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="text-8xl mb-6">🔒</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Purchase Required
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            Please purchase this course to access the subjects and content.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="container">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">
            {subjectName || 'Subjects'}
          </h1>
          <p className="text-white/90">Select a subject to view content</p>
          
          {/* Debug: Show flow type */}
          {type && (
            <div className="mt-3">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                type === 'new' ? 'bg-yellow-500' : 'bg-green-500'
              }`}>
                {type === 'new' ? '4-Screen Flow (New Type)' : '2-Screen Flow (Old Type)'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Subjects Grid - Matching Mobile App Subject Screen */}
      <div className="py-8 sm:py-12 lg:py-16">
        <div className="container">
          {subjects.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
              {subjects.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleSubjectClick(item)}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer group"
                >
                  {/* Subject Image */}
                  {item.images && (
                    <div className="relative h-48 overflow-hidden bg-gray-200">
                      <img 
                        src={getImageUrl(item.images)}
                        alt={item.subname}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x200?text=Subject';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Subject Name - ONLY (matching mobile app) */}
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-gray-800 text-center line-clamp-2">
                      {item.subname}
                    </h3>
                  </div>
                  
                  {/* NO buttons section - matching mobile app Subject screen */}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 sm:py-24">
              <div className="text-6xl sm:text-8xl mb-6">📚</div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-700 mb-3">
                No Subjects Found
              </h3>
              <p className="text-gray-500 text-base sm:text-lg mb-8">
                No subjects available for this course.
              </p>
              <button 
                onClick={() => navigate(-1)} 
                className="btn btn-primary btn-large"
              >
                Go Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subjects;

